import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EntityType, PaymentDueAccountVM } from "@propertelligent/client-api";
import ActionMenu from "../../actionMenus/action-menu";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../emptystate";
import arrowUpIcon from "../../../assets/componentsIcons/listings/arrow_up.svg";
import arrowUpBlueIcon from "../../../assets/componentsIcons/listings/arrow_up_blue.svg";
import dropdownIcon from "../../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../../assets/componentsIcons/dropdown/delete.svg";
import PayableIcon from "../../../assets/svgs/payableIcon";
import LeaseIcon from "../../../assets/svgs/unitIcons/leaseIcon";
import UserIcon from "../../../assets/svgs/unitIcons/userIcon";
import InvoiceIcon from "../../../assets/svgs/payables/arrowDownIcon";
import EmailIcon from "../../../assets/svgs/receivableIcons/emailIcon";
import PhoneIcon from "../../../assets/svgs/receivableIcons/phoneIcon";
import { GetImages } from "../../../utils/GetImages";
import CurrencyTooltip from "../../../components/currencytooltip/CurrencyTooltip";
import BluePayableIcon from "../../../assets/Payment-Icon.svg";
import { usePermissions } from "../../../hooks/usePermissions";
import ArrowOutgoingIcon from "../../../assets/svgs/arrows/arrowOutGoingIcon";
import { ConfirmationPopup } from "../../confirmation-popup/confirmation-popup-demo";
import apiHelper from "../../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../../utils/pubsub";
import {
  deletePaymentDueAccountGroup,
  mapDeleteResponseToAction,
  resolveGroupedPaymentIds,
} from "../../../services/paymentDueAccount.service";
import RightSideModal from "../../Modal/right-side-modal";
import WaivePaymentList from "../../payments/WaivePaymentList";

type GroupedPaymentDueAccount = PaymentDueAccountVM & {
  paymentDueAccountIds?: number[];
};

interface PayableProps {
  id?: any;
  className?: string;
  isFooter?: boolean;
  onRefresh?: () => void;
  onDeleteSuccess?: () => void;
  dataHookState?: {
    rows: PaymentDueAccountVM[];
    loading: boolean;
    fetchData?: any;
  };
  hasAnyPayments?: boolean;
  onAddClick?: () => void;
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: EntityType;
  onEditPayableClick?: (data: any) => void;
  onEditReceivableClick?: (data: any) => void;
  type?: string;
}

const footerData = [
  {
    iconSrc: <UserIcon className="stat-icon" width="24" height="24" />,
    label: "Tenant",
    value: 10,
  },
  { iconSrc: <LeaseIcon className="stat-icon" />, label: "Lease", value: 1 },
];

const PayableCard: React.FC<PayableProps> = ({
  id,
  className = "",
  isFooter = true,
  dataHookState = { rows: [], loading: false },
  hasAnyPayments = false,
  onAddClick,
  isEntity,
  entityId,
  entityTypeId,
  onEditPayableClick,
  onEditReceivableClick,
  type,
  onRefresh,
  onDeleteSuccess,
}) => {
  const { rows, loading, fetchData } = dataHookState;
  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const [paymentToDelete, setPaymentToDelete] =
    useState<GroupedPaymentDueAccount | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [waiveTenantId, setWaiveTenantId] = useState<number>(0);
  const [isWaiveModalOpen, setIsWaiveModalOpen] = useState(false);

  const publishDeleteToast = useCallback(
    (message: string, type: NotificationType) => {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message,
        type,
      });
    },
    [],
  );

  const refreshList = useCallback(async () => {
    if (fetchData) {
      await fetchData();
    }
    onRefresh?.();
  }, [fetchData, onRefresh]);

  const handleDelete = useCallback(async () => {
    if (!paymentToDelete) {
      return;
    }

    const selectedPayment = paymentToDelete;
    setPaymentToDelete(null);
    setIsDeleting(true);

    try {
      const paymentDueAccountIds = resolveGroupedPaymentIds(selectedPayment);

      if (paymentDueAccountIds.length === 0) {
        publishDeleteToast(
          "No payments were available to delete.",
          NotificationType.Warning,
        );
        return;
      }

      const response = await apiHelper(
        deletePaymentDueAccountGroup,
        { showNotification: false },
        paymentDueAccountIds,
      );

      const action = mapDeleteResponseToAction(response);

      if (action.status === "failure") {
        publishDeleteToast(
          "The payment deletion failed. The list has been refreshed.",
          NotificationType.Failure,
        );
        if (action.shouldRefresh) {
          await refreshList();
        }
        return;
      }

      publishDeleteToast(
        action.message ?? "",
        action.notificationType === "success"
          ? NotificationType.Success
          : NotificationType.Warning,
      );
      if (action.status === "success" && action.notificationType === "success" && onDeleteSuccess) {
        onDeleteSuccess();
      } else if (action.shouldRefresh) {
        await refreshList();
      }
    } finally {
      setIsDeleting(false);
    }
  }, [
    paymentToDelete,
    publishDeleteToast,
    refreshList,
    onDeleteSuccess,
  ]);

  // 👇 Helper to generate the query string for navigation
  const getContextParams = () => {
    if (isEntity && entityId) {
      return `&isEntity=${isEntity}&entityId=${entityId}&entityTypeId=${entityTypeId}`;
    }
    return "";
  };

  // Memoized constants
  const PAYABLE_MENU_ITEMS = useMemo(() => {
    const items = [];

    if (canWrite("Payments")) {
      items.push({
        icon: editIcon,
        label: "Edit",
        action: (data: any) => {
          if (data.invoicesCount === 1 && onEditPayableClick) {
            onEditPayableClick(data);
          } else {
            navigate(
              `/payment/edit-payable/${data.id}?pai=${data.payeeAccountId}&paymentType=${String(data?.paidFlowTypeId)}${getContextParams()}`,
            );
          }
        },
        isDivider: false,
        isDestructive: false,
      });

      items.push({ isDivider: true });

      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: (data: GroupedPaymentDueAccount) =>
          setPaymentToDelete(data),
        isDivider: false,
        isDestructive: true,
      });

      items.push({ isDivider: true });

      items.push({
        icon: BluePayableIcon,
        label: "Post Payment",
        action: (data: any) =>
          navigate(
            `/payment/settle-payables-payment/${data.payeeAccountId}?pai=${data.payeeAccountId}&paymentType=${String(data?.paidFlowTypeId)}${getContextParams()}`,
          ),
        isDivider: false,
        isDestructive: false,
      });
    }

    // Remove trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [navigate, canWrite, onEditPayableClick]);

  // Payables owed to Propertelligent (the org's subscription bill) can only be posted, never edited or
  // deleted — the backend enforces this in PaymentDueAccountService.EnsureCanModifyPaymentDueAccountAsync;
  // this mirrors it client-side so the disallowed actions aren't even offered. `isPropertelligentPayable` may
  // be undefined against an older cached client-api build; treat that as "not locked" (fail open, same as any
  // other row) rather than hiding Post Payment for everyone.
  const getPayableMenuItems = useCallback(
    (listingData: PaymentDueAccountVM) => {
      if (!(listingData as any)?.isPropertelligentPayable) {
        return PAYABLE_MENU_ITEMS;
      }

      return PAYABLE_MENU_ITEMS.filter((item) => !item.isDivider && item.label === "Post Payment");
    },
    [PAYABLE_MENU_ITEMS],
  );

  const RECEIVABLE_MENU_ITEMS = useMemo(() => {
    const items = [];

    if (canWrite("Payments")) {
      items.push({
        icon: editIcon,
        label: "Edit",
        action: (data: any) => {
          if (data.invoicesCount === 1 && onEditReceivableClick) {
            onEditReceivableClick(data);
          } else {
            navigate(
              `/payment/edit-receivable/${data.entityId}?tenantId=${data.entityId}&paymentType=${String(data?.paidFlowTypeId)}${getContextParams()}`,
            );
          }
        },
        isDivider: false,
        isDestructive: false,
      });

      items.push({ isDivider: true });

      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: (data: GroupedPaymentDueAccount) =>
          setPaymentToDelete(data),
        isDivider: false,
        isDestructive: true,
      });

      items.push({ isDivider: true });

      items.push({
        icon: BluePayableIcon,
        label: "Post Payment",
        action: (data: any) =>
          navigate(
            `/payment/settle-receivables-payment/${data?.entityId}?tenantId=${data.entityId}&paymentType=${String(data?.paidFlowTypeId)}${getContextParams()}`,
          ),
        isDivider: false,
        isDestructive: false,
      });

      items.push({ isDivider: true });

      items.push({
        icon: BluePayableIcon,
        label: "Waive Payment",
        action: (data: any) => {
          setWaiveTenantId(data?.entityId);
          setIsWaiveModalOpen(true);
        },
        isDivider: false,
        isDestructive: false,
      });

    }

    // Remove trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [navigate, canWrite, onEditReceivableClick]);

  const renderPayableCard = (listingData: PaymentDueAccountVM) => (
    <div id={id} className="listing-card payables" key={listingData.id}>
      <div className={`listing-card-header ${className}`}>
        <div className="listing-image-container">
          <PayableIcon className="listing-image" width="36px" height="36px" />
        </div>
        <div className="listing-details">
          <div className="listing-info-container">
            <div className="listing-info">
              <h3 className="listing-address">{listingData.payerPayeeName}</h3>
              <p className="listing-type">
                {listingData.formatted.PayeeAccountTypeId}
              </p>
            </div>
            <div className="financial-metrics">
              <div className="metric">
                <CurrencyTooltip
                  content={listingData.formatted.AmountRaw}
                  formatted={listingData.formatted.Amount}
                >
                  <p className="metric-value negative">
                    {listingData?.formatted?.Amount}
                  </p>
                </CurrencyTooltip>
                <p className="metric-label">Amount</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isFooter && (
        <>
          <div className="separator-horizontal" />
          <div className="listing-card-footer">
            <div className="listing-stats">
              <div className="stat">
                <InvoiceIcon
                  fill="#4B5563"
                  className="stat-icon"
                  width="24"
                  height="24"
                />
                <span className="stat-text">
                  Outstanding Invoices: {listingData.invoicesCount}
                </span>
              </div>
            </div>

            <div className="footer-actions">
              <div className="footer-highlights">
                {/* Past Due */}
                {listingData?.pastDue > 0 && (
                  <div className="highlight-value negative">
                    <ArrowOutgoingIcon
                      fill="var(--color-danger)"
                      className="highlight-icon"
                      width="20"
                      height="20"
                    />
                    &nbsp;Past Due:&nbsp;
                    <CurrencyTooltip
                      content={listingData.formatted.PastDueRaw}
                      formatted={listingData.formatted.PastDue}
                    >
                      <span className="highlight-text">
                        {listingData?.formatted?.PastDue}
                      </span>
                    </CurrencyTooltip>
                  </div>
                )}

                {/* Due in 30 Days */}
                {listingData?.dueIn30Days > 0 && (
                  <div className="highlight-value info">
                    <ArrowOutgoingIcon
                      fill="var(--color-info)"
                      className="highlight-icon"
                      width="20"
                      height="20"
                    />
                    &nbsp;Due in 30 Days:&nbsp;
                    <CurrencyTooltip
                      content={listingData.formatted.DueIn30DaysRaw}
                      formatted={listingData.formatted.DueIn30Days}
                    >
                      <span className="highlight-text">
                        {listingData?.formatted?.DueIn30Days}
                      </span>
                    </CurrencyTooltip>
                  </div>
                )}
              </div>

              {getPayableMenuItems(listingData).length > 0 && (
                <ActionMenu
                  menuItems={getPayableMenuItems(listingData)}
                  Id={listingData}
                  entityId={listingData.payeeAccountId}
                  trigger={
                    <button className="action-button" aria-label="More options">
                      <img
                        src={dropdownIcon}
                        alt=""
                        width={20}
                        height={20}
                        className="action-icon"
                        loading="lazy"
                      />
                    </button>
                  }
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderReceivableCard = (listingData: PaymentDueAccountVM) => (
    <div id={id} className="listing-card receivables" key={listingData.id}>
      <div className="listing-card-header">
        <div className="listing-image-container">
          <PayableIcon className="listing-image" width="36px" height="36px" />
        </div>
        <div className="listing-details">
          <div className="listing-info-container">
            <div className="listing-info">
              <h3 className="listing-address">
                {type === "tenant"
                  ? listingData.description
                  : listingData.payerPayeeName}
              </h3>
              <p className="listing-type">{listingData.streetAddress}</p>
            </div>
            <div className="financial-metrics">
              <div className="metric">
                <CurrencyTooltip
                  content={listingData.formatted.AmountRaw}
                  formatted={listingData.formatted.Amount}
                >
                  <p className="metric-value negative">
                    {listingData.formatted.Amount}
                  </p>
                </CurrencyTooltip>
                <p className="metric-label">Amount</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isFooter && (
        <>
          <div className="separator-horizontal" />
          <div className="listing-card-footer">
            <div className="listing-stats">
              <div className="stat">
                <EmailIcon
                  fill="#4B5563"
                  className="stat-icon"
                  width="24"
                  height="24"
                />
                <span className="stat-text">Email: {listingData.email}</span>
              </div>
              {listingData?.phoneNumber && (
                <div className="stat">
                  <PhoneIcon
                    fill="#4B5563"
                    className="stat-icon"
                    width="24"
                    height="24"
                  />
                  <span className="stat-text">
                    Phone: {listingData?.formatted?.PhoneNumber}
                  </span>
                </div>
              )}
            </div>

            <div className="footer-actions">
              <div className="footer-highlights">
                {/* Past Due */}
                {Number(
                  String(listingData?.formatted?.PastDueRaw).replace(/,/g, ""),
                ) > 0 && (
                  <div className="highlight-value negative">
                    <ArrowOutgoingIcon
                      fill="currentColor"
                      className="highlight-icon"
                      width="20"
                      height="20"
                    />
                    &nbsp; Past Due:&nbsp;
                    <CurrencyTooltip
                      content={listingData.formatted.PastDueRaw}
                      formatted={listingData.formatted.PastDue}
                    >
                      <span className="highlight-text">
                        {listingData?.formatted?.PastDue}
                      </span>
                    </CurrencyTooltip>
                  </div>
                )}

                {/* Due in 30 Days */}
                {Number(
                  String(listingData?.formatted?.DueIn30DaysRaw).replace(
                    /,/g,
                    "",
                  ),
                ) > 0 && (
                  <div className="highlight-value info">
                    <ArrowOutgoingIcon
                      fill="currentColor"
                      className="highlight-icon"
                      width="20"
                      height="20"
                    />
                    &nbsp; Due in 30 Days:&nbsp;
                    <CurrencyTooltip
                      content={listingData.formatted.DueIn30DaysRaw}
                      formatted={listingData.formatted.DueIn30Days}
                    >
                      <span className="highlight-text">
                        {listingData?.formatted?.DueIn30Days}
                      </span>
                    </CurrencyTooltip>
                  </div>
                )}
              </div>

              {RECEIVABLE_MENU_ITEMS.length > 0 && (
                <ActionMenu
                  menuItems={RECEIVABLE_MENU_ITEMS}
                  Id={listingData}
                  entityId={listingData}
                  trigger={
                    <button className="action-button" aria-label="More options">
                      <img
                        src={dropdownIcon}
                        alt=""
                        width={20}
                        height={20}
                        className="action-icon"
                        loading="lazy"
                      />
                    </button>
                  }
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (rows.length === 0 && !loading) {
    return (
      <EmptyState
        icon={GetImages.emptyStatePayment}
        heading={
          hasAnyPayments ? "No Payments Found" : "Add Your First Payment"
        }
        buttonLabel="Add Payment"
        onButtonClick={onAddClick}
        isButton={hasAnyPayments ? false : true}
      />
    );
  }

  return (
    <>
      <div className={`infinite-scroll-container ${className || ""}`}>
        {rows.map((listingData) =>
          listingData.paidFlowTypeId === 1
            ? renderPayableCard(listingData)
            : renderReceivableCard(listingData),
        )}

        {loading && <SimpleSpinner myClassName="Icon50" />}
      </div>
      <ConfirmationPopup
        isOpen={paymentToDelete !== null}
        title="Delete Payment"
        message="Are you sure you want to delete this payment? Only payments in Due status will be deleted."
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleDelete}
        onSecondaryAction={() => setPaymentToDelete(null)}
        onClose={() => setPaymentToDelete(null)}
        isPrimaryDestructive={true}
        isLoading={isDeleting}
        loadingLabel="Deleting..."
      />
      {waiveTenantId > 0 && (
        <RightSideModal
          isOpen={isWaiveModalOpen}
          onClose={() => {
            setIsWaiveModalOpen(false);
            setWaiveTenantId(0);
          }}
          title="Waive Payment"
          isFooterHidden={false}
          buttonlabel="Waive Payment"
          formId="WaivePayment"
          maxWidth={500}
        >
          <WaivePaymentList
            tenantId={waiveTenantId}
            setIsModalOpen={(value: boolean) => {
              setIsWaiveModalOpen(value);
              if (!value) setWaiveTenantId(0);
            }}
            fetchData={() => {
              refreshList();
            }}
          />
        </RightSideModal>
      )}
    </>
  );
};

export default PayableCard;
